import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, Bell, Mail, Smartphone } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { kvGet, kvSet } from "@/lib/native";
import { toast } from "sonner";

const KEY = "stylio:notification-prefs";
const DEFAULT = {
  daily_brief: true,
  follower_alerts: true,
  outfit_ready: true,
  weekly_digest: false,
  email_marketing: false,
  push_enabled: false,
};

export default function NotificationsSettings() {
  const navigate = useNavigate();
  const [prefs, setPrefs] = useState(DEFAULT);

  useEffect(() => {
    (async () => {
      const stored = await kvGet(KEY);
      if (stored) try { setPrefs({ ...DEFAULT, ...JSON.parse(stored) }); } catch {}
    })();
  }, []);

  const update = async (k, v) => {
    const next = { ...prefs, [k]: v };
    setPrefs(next);
    await kvSet(KEY, JSON.stringify(next));
    toast.success("Preference updated");
  };

  const rows = [
    { k: "daily_brief",     label: "Daily styling brief",      desc: "A handpicked outfit at 8 AM your time.", icon: Bell },
    { k: "follower_alerts", label: "Follower activity",        desc: "When someone follows or likes your look.", icon: Bell },
    { k: "outfit_ready",    label: "AI outfit ready",          desc: "When a generation or try-on finishes.", icon: Bell },
    { k: "weekly_digest",   label: "Weekly digest",            desc: "Top community looks every Sunday.", icon: Mail },
    { k: "email_marketing", label: "Marketing emails",         desc: "New features, drops, partner offers.", icon: Mail },
    { k: "push_enabled",    label: "Push notifications",       desc: "Allow Stylebay to send phone notifications.", icon: Smartphone },
  ];

  return (
    <div className="max-w-2xl mx-auto px-6 lg:px-10 py-10 lg:py-14">
      <button onClick={() => navigate("/profile")} className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground hover:text-foreground inline-flex items-center gap-1" data-testid="back-to-profile">
        <ChevronLeft size={14} /> Account
      </button>
      <p className="eyebrow mt-6 flex items-center gap-2"><Bell size={12} /> Stylebay · Notifications</p>
      <h1 className="editorial-h1 mt-3">Notifications.</h1>
      <p className="text-sm text-muted-foreground mt-3 max-w-md">
        Choose how often Stylebay whispers in your ear.
      </p>

      <div className="mt-10 divide-y divide-border border-y border-border">
        {rows.map((r) => (
          <div key={r.k} className="flex items-center gap-4 px-2 py-4">
            <span className="h-10 w-10 inline-flex items-center justify-center border border-border shrink-0">
              <r.icon size={16} strokeWidth={1.5} />
            </span>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium">{r.label}</p>
              <p className="text-xs text-muted-foreground mt-0.5">{r.desc}</p>
            </div>
            <Switch
              checked={!!prefs[r.k]}
              onCheckedChange={(v) => update(r.k, v)}
              data-testid={`notif-toggle-${r.k}`}
            />
          </div>
        ))}
      </div>
    </div>
  );
}
